import React, { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Linking,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import {
  createPublicSubscriptionCheckoutIntent,
  getPublicSubscriptionBundles,
  startPublicSubscription,
  verifyPublicSubscription,
} from '../src/api/client';
import CountryPicker from '../src/components/CountryPicker';
import WhishMark from '../src/components/payments/WhishMark';
import type { SubscriptionBundle, SubscriptionBundlesResponse, User } from '../src/types/api';
import { COUNTRIES, type Country, buildE164Phone, getCountryByDialCode, parsePhoneDraft } from '../src/utils/countries';
import { FONT_SIZE, RADIUS, SPACING } from '../src/utils/design-tokens';
import { useTheme, type ThemeColors } from '../src/utils/theme';

const DEFAULT_COUNTRY = COUNTRIES.find((c) => c.iso2 === 'LB')!;
const OTP_LENGTH = 6;
const IS_WEB = Platform.OS === 'web';

type Step = 'phone' | 'otp' | 'plans' | 'method' | 'redirecting';

export default function SubscribeScreen() {
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  const webSelectStyle = useMemo(() => ({
    minHeight: 48,
    width: 150,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: colors.border,
    borderRadius: RADIUS.sm,
    padding: `0 ${SPACING.sm}px`,
    color: colors.text,
    backgroundColor: colors.inputBackground,
    fontSize: FONT_SIZE.sm,
    fontFamily: 'Inter, system-ui, sans-serif',
    outline: 'none',
  }), [colors]);
  const webInputStyle = useMemo(() => ({
    minHeight: 48,
    flex: 1,
    minWidth: 0,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: colors.border,
    borderRadius: RADIUS.sm,
    padding: `0 ${SPACING.md}px`,
    color: colors.text,
    backgroundColor: colors.inputBackground,
    fontSize: FONT_SIZE.base,
    fontFamily: 'Inter, system-ui, sans-serif',
    outline: 'none',
  }), [colors]);
  const [step, setStep] = useState<Step>('phone');
  const [country, setCountry] = useState<Country>(DEFAULT_COUNTRY);
  const [phone, setPhone] = useState('');
  const [verifiedPhone, setVerifiedPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  const [user, setUser] = useState<User | null>(null);
  const [bundles, setBundles] = useState<SubscriptionBundle[]>([]);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [whishEnabled, setWhishEnabled] = useState(false);
  const [chosenBundle, setChosenBundle] = useState<SubscriptionBundle | null>(null);
  const [busy, setBusy] = useState(false);
  const [selectedBundleId, setSelectedBundleId] = useState<number | null>(null);
  const [error, setError] = useState('');

  const syncPhoneDraft = useCallback((value: string) => {
    const normalizedIntlDraft = value
      .trim()
      .replace(/^00/, '+')
      .replace(/[^\d+]/g, '')
      .replace(/(?!^)\+/g, '');

    if (normalizedIntlDraft.startsWith('+')) {
      const digits = normalizedIntlDraft.replace(/\D/g, '');
      const matchedCountry = getCountryByDialCode(digits);
      if (!digits || !matchedCountry || digits.length <= matchedCountry.dialCode.length) {
        if (matchedCountry && matchedCountry.iso2 !== country.iso2) setCountry(matchedCountry);
        setPhone(normalizedIntlDraft);
        setError('');
        return { country: matchedCountry || country, localNumber: normalizedIntlDraft, hadExplicitCountryCode: true };
      }
    }

    const parsed = parsePhoneDraft(value, country);
    if (parsed.country.iso2 !== country.iso2) setCountry(parsed.country);
    setPhone(parsed.localNumber);
    setError('');
    return parsed;
  }, [country]);

  const currentFullPhone = useMemo(() => {
    const parsed = parsePhoneDraft(phone, country);
    return buildE164Phone(parsed.localNumber, parsed.country);
  }, [country, phone]);

  const requestCode = async () => {
    const parsed = parsePhoneDraft(phone, country);
    const localDigits = parsed.localNumber.replace(/[^0-9]/g, '');
    if (localDigits.length < 4) {
      setError('Enter your WhatsApp number first.');
      return;
    }

    const fullPhone = buildE164Phone(parsed.localNumber, parsed.country);
    setBusy(true);
    setError('');
    try {
      await startPublicSubscription(fullPhone);
      setVerifiedPhone(fullPhone);
      setOtp('');
      setStep('otp');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not send the verification code. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async () => {
    const code = otp.replace(/[^0-9]/g, '');
    if (code.length !== OTP_LENGTH) {
      setError('Enter the 6-digit code sent on WhatsApp.');
      return;
    }

    setBusy(true);
    setError('');
    try {
      const verified = await verifyPublicSubscription(verifiedPhone || currentFullPhone, code);
      setVerificationToken(verified.token);
      setUser(verified.user);
      const result: SubscriptionBundlesResponse = await getPublicSubscriptionBundles();
      setBundles(result.bundles || []);
      setPaymentsEnabled(!!result.payments?.enabled);
      setWhishEnabled(!!result.payments?.whish?.enabled);
      setStep('plans');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'The code could not be verified. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  // Plan tap: with Whish enabled the user picks a gateway next (in-app, no
  // redirect to the static /pay chooser). With Whish off, go straight to card.
  const handlePlanPress = (bundle: SubscriptionBundle) => {
    if (!paymentsEnabled && !whishEnabled) {
      setError('Payments are not available right now. Please contact support on WhatsApp.');
      return;
    }
    setChosenBundle(bundle);
    setError('');
    if (whishEnabled) {
      setStep('method');
    } else {
      startCheckoutCard(bundle);
    }
  };

  const startCheckoutCard = async (bundle: SubscriptionBundle) => {
    if (!paymentsEnabled) {
      setError('Card payments are not available right now. Please contact support on WhatsApp.');
      return;
    }
    setBusy(true);
    setSelectedBundleId(bundle.id);
    setError('');
    try {
      const intent = await createPublicSubscriptionCheckoutIntent({
        token: verificationToken,
        bundleId: bundle.id,
      });
      setStep('redirecting');
      // Land DIRECTLY on /pay/card - we already picked the gateway in-app,
      // so the static /pay chooser would only show a redundant step.
      const cardUrl = intent.checkoutUrl.replace(/\/pay(?=\?|$)/, '/pay/card');
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.location.assign(cardUrl);
      } else {
        await Linking.openURL(cardUrl);
      }
    } catch (err: any) {
      setStep('plans');
      setError(err?.response?.data?.error || 'Could not start checkout. Please verify again and retry.');
    } finally {
      setBusy(false);
      setSelectedBundleId(null);
    }
  };

  const startCheckoutWhish = async (bundle: SubscriptionBundle) => {
    if (!whishEnabled) {
      setError('Whish wallet is not available right now.');
      return;
    }
    setBusy(true);
    setSelectedBundleId(bundle.id);
    setError('');
    try {
      // On web we can just send the user back to the FishMemory app root
      // after success. The backend whitelists fishmemory:// and the prod
      // host - any other returnUrl is silently dropped.
      const returnUrl = (Platform.OS === 'web' && typeof window !== 'undefined')
        ? 'https://app.fishmemory.net/'
        : '';
      const res = await fetch('/api/v1/payments/whish/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: verificationToken, bundleId: bundle.id, returnUrl }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.collectUrl) {
        throw new Error(data?.error || 'Whish refused to start the payment.');
      }
      setStep('redirecting');
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.location.assign(data.collectUrl);
      } else {
        await Linking.openURL(data.collectUrl);
      }
    } catch (err: any) {
      setStep('method');
      setError(err?.message || 'Could not start Whish. Try the card option, or try again.');
    } finally {
      setBusy(false);
      setSelectedBundleId(null);
    }
  };

  const resetToPhone = () => {
    setStep('phone');
    setOtp('');
    setVerificationToken('');
    setError('');
    setBundles([]);
  };

  return (
    <ScrollView style={s.page} contentContainerStyle={s.pageContent} keyboardShouldPersistTaps="handled">
      <View style={s.shell}>
        <View style={s.brand}>
          <Image source={require('../assets/logo.png')} style={s.logo} />
          <Text style={s.brandName}>FishMemory</Text>
        </View>

        <View style={s.hero}>
          <Text style={s.title}>Subscribe or renew</Text>
          <Text style={s.subtitle}>
            Verify your WhatsApp number, choose a plan, and pay securely{whishEnabled ? ' by card or Whish wallet.' : ' by card.'}
          </Text>
        </View>

        <View style={s.steps}>
          {['Number', 'Code', 'Plan'].map((label, index) => {
            const activeIndex = step === 'phone' ? 0 : step === 'otp' ? 1 : 2;
            const active = index <= activeIndex;
            return (
              <View key={label} style={s.stepItem}>
                <View style={[s.stepDot, active && s.stepDotActive]}>
                  <Text style={[s.stepNumber, active && s.stepNumberActive]}>{index + 1}</Text>
                </View>
                <Text style={[s.stepLabel, active && s.stepLabelActive]}>{label}</Text>
              </View>
            );
          })}
        </View>

        {step === 'phone' && (
          <View style={s.panel}>
            <Text style={s.panelTitle}>Enter your WhatsApp number</Text>
            <Text style={s.panelText}>We will send a 6-digit code on WhatsApp to confirm this account.</Text>
            {IS_WEB ? (
              <View style={s.phoneRow}>
                {React.createElement(
                  'select',
                  {
                    value: country.iso2,
                    onChange: (event: any) => {
                      const next = COUNTRIES.find((c) => c.iso2 === event.currentTarget.value);
                      if (next) setCountry(next);
                      setError('');
                    },
                    style: webSelectStyle,
                    'aria-label': 'Country code',
                  },
                  COUNTRIES.map((item) => React.createElement(
                    'option',
                    { key: item.iso2, value: item.iso2 },
                    `${item.flag} +${item.dialCode} ${item.name}`,
                  )),
                )}
                {React.createElement('input', {
                  value: phone,
                  onChange: (event: any) => syncPhoneDraft(event.currentTarget.value),
                  onFocus: () => setError(''),
                  type: 'tel',
                  inputMode: 'tel',
                  autoComplete: 'tel',
                  placeholder: 'WhatsApp number',
                  style: webInputStyle,
                  'aria-label': 'WhatsApp number',
                })}
              </View>
            ) : (
              <View style={s.phoneRow}>
                <CountryPicker selectedCountry={country} onSelect={setCountry} />
                <TextInput
                  style={[s.input, s.phoneInput]}
                  value={phone}
                  onChangeText={syncPhoneDraft}
                  keyboardType="phone-pad"
                  placeholder="WhatsApp number"
                  placeholderTextColor={colors.textSecondary}
                  textContentType="telephoneNumber"
                  accessibilityLabel="WhatsApp number"
                  onFocus={() => setError('')}
                />
              </View>
            )}
            <PrimaryButton
              label="Send code"
              icon="logo-whatsapp"
              busy={busy}
              onPress={requestCode}
              colors={colors}
            />
          </View>
        )}

        {step === 'otp' && (
          <View style={s.panel}>
            <Text style={s.panelTitle}>Enter verification code</Text>
            <Text style={s.panelText}>Code sent to {verifiedPhone}. It expires in 5 minutes.</Text>
            <TextInput
              style={[s.input, s.otpInput]}
              value={otp}
              onChangeText={(value) => {
                setOtp(value.replace(/[^0-9]/g, '').slice(0, OTP_LENGTH));
                setError('');
              }}
              keyboardType="number-pad"
              placeholder="000000"
              placeholderTextColor={colors.textSecondary}
              maxLength={OTP_LENGTH}
              textContentType="oneTimeCode"
            />
            <PrimaryButton
              label="Verify number"
              icon="checkmark-circle-outline"
              busy={busy}
              onPress={verifyCode}
              colors={colors}
            />
            <TouchableOpacity style={s.secondaryButton} onPress={resetToPhone} disabled={busy}>
              <Text style={s.secondaryButtonText}>Use a different number</Text>
            </TouchableOpacity>
          </View>
        )}

        {(step === 'plans' || step === 'redirecting') && (
          <View style={s.panel}>
            <Text style={s.panelTitle}>Choose a plan</Text>
            <Text style={s.panelText}>
              {user?.subStatus === 'active'
                ? 'Your plan is active. Choose a bundle to extend it.'
                : 'Your subscription will activate as soon as payment succeeds.'}
            </Text>
            {user && (
              <View style={s.statusBox}>
                <Ionicons name={user.hasPaidBefore ? 'card-outline' : 'time-outline'} size={18} color={colors.primary} />
                <View style={{ flex: 1 }}>
                  <Text style={s.statusTitle}>{subscriptionStatusTitle(user)}</Text>
                  <Text style={s.statusText}>{subscriptionStatusText(user)}</Text>
                </View>
              </View>
            )}
            {!paymentsEnabled && (
              <View style={s.notice}>
                <Ionicons name="alert-circle-outline" size={18} color={colors.warning} />
                <Text style={s.noticeText}>Card payments are currently unavailable. Please contact support on WhatsApp.</Text>
              </View>
            )}
            {bundles.length === 0 ? (
              <Text style={s.emptyText}>No plans are available right now.</Text>
            ) : (
              bundles.map((bundle) => (
                <TouchableOpacity
                  key={bundle.id}
                  style={s.planRow}
                  onPress={() => handlePlanPress(bundle)}
                  disabled={busy || (!paymentsEnabled && !whishEnabled)}
                  accessibilityRole="button"
                  accessibilityLabel={`Subscribe to ${bundle.label}`}
                >
                  <View style={{ flex: 1 }}>
                    <Text style={s.planTitle}>{bundle.label}</Text>
                    <Text style={s.planMeta}>{formatDuration(bundle.durationMonths)} access</Text>
                  </View>
                  <View style={s.planAction}>
                    {selectedBundleId === bundle.id ? (
                      <ActivityIndicator size="small" color="#fff" />
                    ) : (
                      <>
                        <Text style={s.planPrice}>{formatPrice(bundle.priceUsd)}</Text>
                        <Ionicons name="chevron-forward" size={17} color="#fff" />
                      </>
                    )}
                  </View>
                </TouchableOpacity>
              ))
            )}
          </View>
        )}

        {step === 'method' && chosenBundle && (
          <View style={s.panel}>
            <Text style={s.panelTitle}>Choose how to pay</Text>
            <Text style={s.panelHelper}>
              {`${chosenBundle.label} · $${chosenBundle.priceUsd.toFixed(2)} ${formatDuration(chosenBundle.durationMonths)} access`}
            </Text>
            {!!paymentsEnabled && (
              <TouchableOpacity
                style={s.methodOpt}
                onPress={() => startCheckoutCard(chosenBundle)}
                disabled={busy}
                accessibilityRole="button"
                accessibilityLabel="Pay by card"
              >
                <View style={s.methodIcon}>
                  <Ionicons name="card" size={22} color={colors.primary} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={s.methodTitle}>Card</Text>
                  <Text style={s.methodSub}>Visa or Mastercard, secured by CyberSource</Text>
                </View>
                {selectedBundleId === chosenBundle.id && busy ? (
                  <ActivityIndicator size="small" color={colors.primary} />
                ) : (
                  <Ionicons name="chevron-forward" size={18} color={colors.textSecondary} />
                )}
              </TouchableOpacity>
            )}
            {!!whishEnabled && (
              <TouchableOpacity
                style={s.methodOpt}
                onPress={() => startCheckoutWhish(chosenBundle)}
                disabled={busy}
                accessibilityRole="button"
                accessibilityLabel="Pay with Whish wallet"
              >
                <View style={s.methodIcon}>
                  <WhishMark size="sm" />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={s.methodTitle}>Whish wallet</Text>
                  <Text style={s.methodSub}>Pay from your Whish app with your phone number</Text>
                </View>
                {selectedBundleId === chosenBundle.id && busy ? (
                  <ActivityIndicator size="small" color={colors.primary} />
                ) : (
                  <Ionicons name="chevron-forward" size={18} color={colors.textSecondary} />
                )}
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={s.methodBack}
              onPress={() => { setChosenBundle(null); setStep('plans'); }}
              disabled={busy}
              accessibilityRole="button"
            >
              <Text style={s.methodBackText}>Back to plans</Text>
            </TouchableOpacity>
          </View>
        )}

        {step === 'redirecting' && (
          <View style={s.redirecting}>
            <ActivityIndicator size="small" color={colors.primary} />
            <Text style={s.redirectingText}>Opening secure checkout...</Text>
          </View>
        )}

        {!!error && (
          <View style={s.errorBox}>
            <Ionicons name="alert-circle-outline" size={17} color={colors.error} />
            <Text style={s.errorText}>{error}</Text>
          </View>
        )}

        <View style={s.footer}>
          <Text style={s.footerText}>Already logged in?</Text>
          <TouchableOpacity onPress={() => Linking.openURL('https://app.fishmemory.net')}>
            <Text style={s.footerLink}>Open the full app</Text>
          </TouchableOpacity>
        </View>
      </View>
    </ScrollView>
  );
}

function PrimaryButton({
  label,
  icon,
  busy,
  onPress,
  colors,
}: {
  label: string;
  icon: keyof typeof Ionicons.glyphMap;
  busy: boolean;
  onPress: () => void;
  colors: ThemeColors;
}) {
  return (
    <TouchableOpacity
      style={[buttonStyles.primary, { backgroundColor: colors.primary }, busy && { opacity: 0.7 }]}
      onPress={onPress}
      disabled={busy}
      accessibilityRole="button"
      accessibilityLabel={label}
    >
      {busy ? <ActivityIndicator size="small" color="#fff" /> : <Ionicons name={icon} size={18} color="#fff" />}
      <Text style={buttonStyles.primaryText}>{label}</Text>
    </TouchableOpacity>
  );
}

function formatPrice(value: number) {
  return `$${Number(value || 0).toFixed(2)}`;
}

function formatDuration(months: number) {
  const value = Number(months || 0);
  if (value === 1) return '1 month';
  return `${value} months`;
}

function subscriptionStatusTitle(user: User) {
  switch (user.customerLifecycle) {
    case 'paid_active': return 'Your paid subscription is active';
    case 'paid_expired': return 'Your paid subscription expired';
    case 'trial_active': return 'Your free trial is active';
    case 'trial_expired': return 'Your trial ended';
    case 'blocked': return 'This account is blocked';
    default:
      return user.hasPaidBefore ? 'Returning subscriber' : 'New subscription';
  }
}

function subscriptionStatusText(user: User) {
  const until = user.subUntilUtc ? new Date(user.subUntilUtc).toLocaleDateString() : '';
  switch (user.customerLifecycle) {
    case 'paid_active':
      return until ? `You can renew now to extend access beyond ${until}.` : 'Choose a plan to extend your access.';
    case 'paid_expired':
      return until ? `Your paid access ended on ${until}. Choose a plan to renew.` : 'Choose a plan to renew your paid access.';
    case 'trial_active':
      return until ? `Your trial ends on ${until}. Subscribe now to keep access active.` : 'Subscribe now to keep access active after your trial.';
    case 'trial_expired':
      return 'You have not subscribed yet. Choose a plan to activate your account.';
    default:
      return 'Choose a plan below and your subscription will update after payment succeeds.';
  }
}

const buttonStyles = StyleSheet.create({
  primary: {
    minHeight: 48,
    borderRadius: RADIUS.sm,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: SPACING.sm,
    marginTop: SPACING.lg,
  },
  primaryText: {
    color: '#fff',
    fontSize: FONT_SIZE.base,
    fontWeight: '700',
  },
});

function createStyles(colors: ThemeColors) {
  return StyleSheet.create({
    page: {
      flex: 1,
      backgroundColor: colors.background,
    },
    pageContent: {
      minHeight: '100%',
      padding: SPACING.lg,
      alignItems: 'center',
      justifyContent: 'center',
    },
    shell: {
      width: '100%',
      maxWidth: 560,
    },
    brand: {
      alignItems: 'center',
      marginBottom: SPACING.xl,
    },
    logo: {
      width: 58,
      height: 58,
      borderRadius: RADIUS.md,
      marginBottom: SPACING.sm,
    },
    brandName: {
      color: colors.text,
      fontSize: FONT_SIZE.lg,
      fontWeight: '800',
    },
    hero: {
      marginBottom: SPACING.xl,
    },
    title: {
      color: colors.text,
      fontSize: IS_WEB ? FONT_SIZE['3xl'] : FONT_SIZE['2xl'],
      fontWeight: '800',
      textAlign: 'center',
      letterSpacing: 0,
    },
    subtitle: {
      color: colors.textSecondary,
      fontSize: FONT_SIZE.base,
      lineHeight: 24,
      textAlign: 'center',
      marginTop: SPACING.sm,
    },
    steps: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: SPACING.lg,
    },
    stepItem: {
      flex: 1,
      alignItems: 'center',
      gap: SPACING.xs,
    },
    stepDot: {
      width: 30,
      height: 30,
      borderRadius: RADIUS.full,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
    },
    stepDotActive: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    stepNumber: {
      color: colors.textSecondary,
      fontSize: FONT_SIZE.sm,
      fontWeight: '700',
    },
    stepNumberActive: {
      color: '#fff',
    },
    stepLabel: {
      color: colors.textSecondary,
      fontSize: FONT_SIZE.sm,
      fontWeight: '600',
    },
    stepLabelActive: {
      color: colors.text,
    },
    panel: {
      backgroundColor: colors.card,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: RADIUS.sm,
      padding: SPACING.lg,
    },
    panelTitle: {
      color: colors.text,
      fontSize: FONT_SIZE.lg,
      fontWeight: '800',
      marginBottom: SPACING.xs,
    },
    panelText: {
      color: colors.textSecondary,
      fontSize: FONT_SIZE.sm,
      lineHeight: 20,
      marginBottom: SPACING.lg,
    },
    panelHelper: {
      color: colors.textSecondary,
      fontSize: FONT_SIZE.sm,
      marginBottom: SPACING.md,
    },
    methodOpt: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: SPACING.md,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: RADIUS.sm,
      padding: SPACING.md,
      marginBottom: SPACING.sm,
      backgroundColor: colors.inputBackground,
    },
    methodIcon: {
      width: 64,
      height: 40,
      borderRadius: 14,
      backgroundColor: colors.card,
      alignItems: 'center',
      justifyContent: 'center',
    },
    methodTitle: {
      color: colors.text,
      fontSize: FONT_SIZE.base,
      fontWeight: '700',
    },
    methodSub: {
      color: colors.textSecondary,
      fontSize: FONT_SIZE.xs,
      marginTop: 2,
    },
    methodBack: {
      alignSelf: 'center',
      paddingVertical: SPACING.sm,
      marginTop: SPACING.xs,
    },
    methodBackText: {
      color: colors.textSecondary,
      fontSize: FONT_SIZE.sm,
      fontWeight: '600',
    },
    phoneRow: {
      flexDirection: 'row',
      gap: SPACING.sm,
      alignItems: 'stretch',
      ...(IS_WEB ? { overflow: 'visible' as any } : {}),
    },
    input: {
      flex: 1,
      minHeight: 48,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: RADIUS.sm,
      paddingHorizontal: SPACING.md,
      color: colors.text,
      backgroundColor: colors.inputBackground,
      fontSize: FONT_SIZE.base,
    },
    phoneInput: {
      flex: 1,
      width: IS_WEB ? '100%' : 0,
      minWidth: 0,
    },
    otpInput: {
      flex: 0,
      textAlign: 'center',
      letterSpacing: 0,
      fontSize: FONT_SIZE.xl,
      fontWeight: '800',
    },
    secondaryButton: {
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 42,
      marginTop: SPACING.sm,
    },
    secondaryButtonText: {
      color: colors.primary,
      fontSize: FONT_SIZE.sm,
      fontWeight: '700',
    },
    notice: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: SPACING.sm,
      padding: SPACING.md,
      borderRadius: RADIUS.sm,
      backgroundColor: colors.warning + '12',
      marginBottom: SPACING.md,
    },
    noticeText: {
      flex: 1,
      color: colors.text,
      fontSize: FONT_SIZE.sm,
      lineHeight: 20,
    },
    emptyText: {
      color: colors.textSecondary,
      fontSize: FONT_SIZE.sm,
    },
    planRow: {
      minHeight: 72,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: RADIUS.sm,
      padding: SPACING.md,
      marginTop: SPACING.sm,
      flexDirection: 'row',
      alignItems: 'center',
      gap: SPACING.md,
      backgroundColor: colors.surface,
    },
    planTitle: {
      color: colors.text,
      fontSize: FONT_SIZE.base,
      fontWeight: '800',
    },
    planMeta: {
      color: colors.textSecondary,
      fontSize: FONT_SIZE.sm,
      marginTop: 2,
    },
    planAction: {
      minWidth: 104,
      minHeight: 40,
      borderRadius: RADIUS.sm,
      backgroundColor: colors.primary,
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'row',
      gap: SPACING.xs,
      paddingHorizontal: SPACING.sm,
    },
    planPrice: {
      color: '#fff',
      fontSize: FONT_SIZE.sm,
      fontWeight: '800',
    },
    redirecting: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: SPACING.sm,
      marginTop: SPACING.md,
    },
    redirectingText: {
      color: colors.textSecondary,
      fontSize: FONT_SIZE.sm,
      fontWeight: '600',
    },
    errorBox: {
      marginTop: SPACING.md,
      padding: SPACING.md,
      borderRadius: RADIUS.sm,
      borderWidth: 1,
      borderColor: colors.error + '33',
      backgroundColor: colors.error + '0A',
      flexDirection: 'row',
      gap: SPACING.sm,
      alignItems: 'flex-start',
    },
    statusBox: {
      flexDirection: 'row',
      gap: SPACING.sm,
      padding: SPACING.md,
      borderRadius: RADIUS.sm,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
      marginBottom: SPACING.md,
      alignItems: 'flex-start',
    },
    statusTitle: {
      color: colors.text,
      fontSize: FONT_SIZE.sm,
      fontWeight: '800',
      marginBottom: 2,
    },
    statusText: {
      color: colors.textSecondary,
      fontSize: FONT_SIZE.sm,
      lineHeight: 20,
    },
    errorText: {
      flex: 1,
      color: colors.error,
      fontSize: FONT_SIZE.sm,
      lineHeight: 20,
      fontWeight: '600',
    },
    footer: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      gap: SPACING.xs,
      marginTop: SPACING.xl,
      flexWrap: 'wrap',
    },
    footerText: {
      color: colors.textSecondary,
      fontSize: FONT_SIZE.sm,
    },
    footerLink: {
      color: colors.primary,
      fontSize: FONT_SIZE.sm,
      fontWeight: '800',
    },
  });
}
